import { useState } from "react";
import { kannZuweisen } from "@/components/plugin-catalog/pluginGate";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Blocks } from "lucide-react";
import type { Agent, AgentPluginSummary } from "@/types";
import { SectionCard } from "@/components/SectionCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ListToolbar } from "@/components/ListToolbar";
import { useAgentPlugins } from "@/api/queries/plugins";
import { useSetAgentPlugin, useUpdatePluginInBrain } from "@/api/mutations/plugins";
import { CatalogSourceAlert, PluginStatusBadge } from "@/components/plugin-catalog/CatalogStatus";

// #232 (UC-4): Plugins pro Agent — enable/disable + Scope (managed erzwungen | owner freigegeben)
// + Drift (Soll[enabled] vs. Ist[installed laut letztem Inventory-Snapshot]).
// Verfügbarkeit (enable) ist orthogonal zur Permission (allow/ask/deny via Settings).
// #287: Der Katalog kommt live aus der marketplace.json. `status` sagt, ob ein Eintrag noch
// drinsteht (verfügbar), verschwunden ist (verwaist → wird nicht mehr ausgeliefert, Zuweisung
// bleibt) oder ob die Quelle gerade nicht lesbar war (dann wird NICHTS weggenommen).
export function AgentPluginsSection({ agent }: { agent: Agent }) {
  const { data, isLoading, isError, error } = useAgentPlugins(agent.id);
  const setEnabled = useSetAgentPlugin(agent.id);
  const updateInBrain = useUpdatePluginInBrain(); // #294 (UC-3)
  const agentId = agent.id;
  const [search, setSearch] = useState("");

  const needle = search.trim().toLowerCase();
  const filtered = (data?.plugins ?? []).filter(
    (p) => p.name.toLowerCase().includes(needle) || p.marketplaceName.toLowerCase().includes(needle),
  );

  function driftChip(p: AgentPluginSummary) {
    // #288: Steht VOR allem anderen. Ein gesperrtes Plugin wird nicht mehr ausgeliefert —
    // egal, was Zuweisung und Katalog sagen. Zeigte die Zeile hier „aktiv", suchte der Admin
    // die Ursache beim Agenten statt bei der Freigabe, die er selbst zurückgenommen hat.
    if (p.enabled && !p.approved) {
      return (
        <span
          className="text-[11.5px] text-danger"
          title="Die Plattform-Freischaltung wurde zurückgenommen — das Plugin wird beim nächsten Push aus dem Brain entfernt. Die Zuweisung bleibt bestehen und lebt wieder auf, sobald das Plugin erneut freigeschaltet wird."
        >
          ⛔ gesperrt — wird entfernt
        </span>
      );
    }
    // ⚠ „Verwaist" wird ZUERST geprüft. Vorher stand `enabled && installed` davor — ein aus
    // dem Katalog verschwundenes Plugin, das der Brain noch hat, zeigte damit „● aktiv" und
    // sah ausgerechnet in dem Moment am gesündesten aus, in dem es gleich verschwindet.
    if (p.enabled && p.status === "orphaned") {
      return (
        <span
          className="text-[11.5px] text-warn"
          title="Aus der marketplace.json verschwunden — wird nicht mehr ausgeliefert. Kommt der Eintrag zurück, lebt die Zuweisung von selbst wieder auf."
        >
          ⚠ nicht mehr im Katalog
        </span>
      );
    }
    // #294 (UC-2): Update-Signal VOR „aktiv" — installiert ≠ Katalog-Version. Der
    // Aktualisieren-Knopf daneben rollt in-place aus (idempotent, kein Neustart).
    if (p.enabled && p.installed && p.updateAvailable) {
      return (
        <span className="flex items-center gap-1.5">
          <span className="text-[11.5px] text-warn" data-testid={`plugin-update-chip-${p.name}`}>
            ↑ Update: {p.installedVersion ?? "?"} → {p.version ?? "?"}
          </span>
          <button
            className="rounded border border-border px-1.5 py-0.5 text-[11px] text-accent-text hover:underline disabled:opacity-50"
            // Review L1 (=#291-Fund 8): nur die ANGEKLICKTE Zeile sperren, nicht alle.
            disabled={updateInBrain.isPending && updateInBrain.variables?.pluginName === p.name}
            onClick={() =>
              updateInBrain.mutate(
                { agentId, marketplaceId: p.marketplaceId, pluginName: p.name },
                {
                  onSuccess: (r) => toast.success(`${p.name} aktualisiert${r.installedVersion ? ` (${r.installedVersion})` : ""}.`),
                  onError: (err) => toast.error(`Update fehlgeschlagen: ${(err as Error).message}`),
                },
              )
            }
            data-testid={`plugin-update-btn-${p.name}`}
          >
            {updateInBrain.isPending && updateInBrain.variables?.pluginName === p.name ? "läuft …" : "Aktualisieren"}
          </button>
        </span>
      );
    }
    // Installierte Version immer mit anzeigen (Arben-Wunsch #294) — nicht nur im Update-Fall.
    if (p.enabled && p.installed)
      return (
        <span className="text-[11.5px] text-ok-text" data-testid={`plugin-active-${p.name}`}>
          ● aktiv{p.installedVersion ? <span className="text-text3"> · v{p.installedVersion}</span> : null}
        </span>
      );
    if (p.enabled && !p.installed) return <span className="text-[11.5px] text-warn">○ wird installiert</span>;
    if (!p.enabled && p.installed)
      return (
        <span className="text-[11.5px] text-warn" title="Brain hat das Plugin noch — wird beim nächsten Turn entfernt">
          ⚠ noch im Brain{p.installedVersion ? ` · v${p.installedVersion}` : ""}
        </span>
      );
    return <span className="text-[11.5px] text-text3">—</span>;
  }

  function toggle(p: AgentPluginSummary, enabled: boolean) {
    setEnabled.mutate(
      { marketplaceId: p.marketplaceId, pluginName: p.name, enabled, scope: p.scope ?? "managed" },
      {
        onSuccess: () => toast.success(`${p.name} ${enabled ? "aktiviert" : "deaktiviert"}`),
        onError: (err) => toast.error(`Fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  function changeScope(p: AgentPluginSummary, scope: "managed" | "owner") {
    setEnabled.mutate(
      { marketplaceId: p.marketplaceId, pluginName: p.name, enabled: true, scope },
      {
        onSuccess: () => toast.success(`${p.name}: Scope ${scope}`),
        onError: (err) => toast.error(`Fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  return (
    <SectionCard
      icon={<Blocks className="h-[18px] w-[18px]" />}
      title="Plugins"
      description="Marketplace-Plugins aktivieren (Scope managed = erzwungen, owner = vom User steuerbar)"
      action={
        <Link to="/plugins" className="text-[12.5px] font-semibold text-accent-text hover:underline">
          Katalog →
        </Link>
      }
    >
      <CatalogSourceAlert sources={data?.sources} />
      {isLoading ? (
        <p className="text-[13px] text-text3">Lade …</p>
      ) : isError ? (
        <p className="text-[13px] text-danger">Plugins konnten nicht geladen werden: {(error as Error | undefined)?.message}</p>
      ) : (data?.plugins.length ?? 0) === 0 ? (
        <p className="text-[13px] text-text3">
          Keine Plugins im Katalog. Lege im <Link to="/plugins" className="text-accent-text hover:underline">Plugin-Katalog</Link> Marketplaces an.
        </p>
      ) : (
        <>
          <ListToolbar search={search} onSearch={setSearch} placeholder="Plugin suchen…" />
          {filtered.length === 0 ? (
            <p className="text-[13px] text-text3">Kein Plugin passt zur Suche.</p>
          ) : (
        <ul className="space-y-1.5" data-testid="agent-plugins-list">
          {filtered.map((p) => (
            <li key={p.ref} className="flex items-center gap-3 rounded-md border border-border px-2.5 py-2">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={p.enabled}
                  // #287 (UC-1): Aktivieren geht nur, was im Manifest steht. Abschalten immer —
                  // sonst säße eine verwaiste Zuweisung für immer fest.
                  // #288 (UC-2): Und nur, was plattformseitig freigegeben ist.
                  disabled={setEnabled.isPending || !kannZuweisen(p)}
                  onChange={(e) => toggle(p, e.target.checked)}
                  data-testid={`agent-plugin-toggle-${p.marketplaceId}-${p.name}`}
                />
                <span className="text-[13px] font-medium text-text">{p.name}</span>
              </label>
              <span className="mono text-[11px] text-text3" title={p.ref}>{p.marketplaceName}</span>
              {p.status !== "available" && <PluginStatusBadge status={p.status} />}
              {/* #288: Nicht freigeschaltet ist ein eigener Zustand — nicht dasselbe wie
                  „verwaist". Der Admin sieht es hier und schaltet es im Katalog frei. */}
              {!p.approved && (
                <Link
                  to="/plugins"
                  className="text-[11px] font-medium text-warn hover:underline"
                  title="Dieses Plugin ist plattformseitig nicht freigeschaltet und kann deshalb nicht zugewiesen werden. Im Plugin-Katalog freischalten."
                >
                  nicht freigeschaltet
                </Link>
              )}
              <span className="flex-1">{driftChip(p)}</span>
              {p.enabled && (
                <Select value={p.scope ?? "managed"} onValueChange={(v) => changeScope(p, v as "managed" | "owner")}>
                  <SelectTrigger className="h-7 w-[110px] text-[12px]" data-testid={`agent-plugin-scope-${p.marketplaceId}-${p.name}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="managed">managed</SelectItem>
                    <SelectItem value="owner">owner</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </li>
          ))}
        </ul>
          )}
        </>
      )}
    </SectionCard>
  );
}
